// Clip-length piece for the Make-clips popup. Two states:
//   loading            → slim "Detecting length…" row
//   finite duration     → dual-handle trimmer + quick presets
//   otherwise           → nothing (no VOD = no clip-length UI)
// Styles live in ForYouPage/forYou.css (mkc-* + reused mkc-chip) so it's
// visually identical to the rest of the panel.
import { useRef } from "react";

const MIN_GAP = 60; // seconds — handles can't come within a minute of each other

function fmt(sec) {
  const s = Math.max(0, Math.floor(sec || 0));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const ss = s % 60;
  return `${h}:${String(m).padStart(2, "0")}:${String(ss).padStart(2, "0")}`;
}

function Scissors() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="6" cy="6" r="3" /><circle cx="6" cy="18" r="3" />
      <line x1="20" y1="4" x2="8.12" y2="15.88" /><line x1="14.47" y1="14.48" x2="20" y2="20" /><line x1="8.12" y1="8.12" x2="12" y2="12" />
    </svg>
  );
}

export default function ClipLengthTrimmer({ loading, duration, start, end, onChange }) {
  const trackRef = useRef(null);
  const dragRef = useRef(null); // "start" | "end" | null

  if (loading) {
    return (
      <div className="mkc-detecting">
        <span className="mkc-detecting-ic"><Scissors /></span>
        <span className="mkc-detecting-label">Detecting length…</span>
        <span className="mkc-detecting-spin" />
      </div>
    );
  }
  if (!(Number.isFinite(duration) && duration > 0)) return null;

  const st = Math.max(0, start ?? 0);
  const en = Math.min(duration, end ?? duration);

  const valueFromX = (clientX) => {
    const rect = trackRef.current?.getBoundingClientRect();
    if (!rect || !rect.width) return 0;
    const ratio = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width));
    return Math.round(ratio * duration);
  };

  const onDown = (which) => (e) => {
    e.preventDefault();
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch (_) {}
    dragRef.current = which;
  };
  const onMove = (e) => {
    if (!dragRef.current) return;
    const v = valueFromX(e.clientX);
    if (dragRef.current === "start") onChange(Math.max(0, Math.min(v, en - MIN_GAP)), en);
    else onChange(st, Math.min(duration, Math.max(v, st + MIN_GAP)));
  };
  const onUp = (e) => {
    dragRef.current = null;
    try { e.currentTarget.releasePointerCapture(e.pointerId); } catch (_) {}
  };

  const pct = (v) => `${(v / duration) * 100}%`;

  const presets = [
    { label: "Full", end: duration },
    { label: "First 15m", end: Math.min(900, duration) },
    { label: "First 30m", end: Math.min(1800, duration) },
    { label: "First 1h", end: Math.min(3600, duration) },
  ];
  const activeLabel = st !== 0
    ? null
    : en === duration
      ? "Full"
      : (presets.find((p) => p.label !== "Full" && p.end === en)?.label ?? null);

  return (
    <div className="mkc-cl">
      <div className="mkc-cl-head">
        <span className="mkc-cl-head-ic"><Scissors /></span>
        <span className="mkc-cl-head-label">CLIP LENGTH</span>
      </div>

      <div className="mkc-cl-slider">
        <div className="mkc-cl-track" ref={trackRef}>
          <div className="mkc-cl-fill" style={{ left: pct(st), width: pct(en - st) }} />
        </div>
        <div
          className="mkc-cl-handle" style={{ left: pct(st) }}
          onPointerDown={onDown("start")} onPointerMove={onMove} onPointerUp={onUp} onPointerCancel={onUp}
          role="slider" aria-label="Clip start" aria-valuenow={Math.round(st)}
        />
        <div
          className="mkc-cl-handle" style={{ left: pct(en) }}
          onPointerDown={onDown("end")} onPointerMove={onMove} onPointerUp={onUp} onPointerCancel={onUp}
          role="slider" aria-label="Clip end" aria-valuenow={Math.round(en)}
        />
      </div>

      <div className="mkc-cl-times">
        <span>{fmt(st)}</span>
        <span className="mkc-cl-times-of">of {fmt(duration)}</span>
        <span>{fmt(en)}</span>
      </div>

      <div className="mkc-cl-presets">
        {presets.map((p) => (
          <button
            key={p.label}
            type="button"
            className={`mkc-chip${activeLabel === p.label ? " on" : ""}`}
            onClick={() => onChange(0, p.end)}
          >
            {p.label}
          </button>
        ))}
      </div>
    </div>
  );
}
